import { computed, unref, type MaybeRef } from "vue";
import { createQuery } from "@/queries";
import { shoppingList } from "@/features/search/api/search";
import type { ShoppingListResponse, shoppingListParams, shoppingListPayload } from "@/types/searchItemsTypes";

export const useShoppingListQuery = (
  payload: MaybeRef<shoppingListPayload | null | undefined>,
  params: MaybeRef<shoppingListParams | null | undefined>,
  enabled?: MaybeRef<boolean | undefined>,
  chainId?: MaybeRef<string | null | undefined>,
  signal?: MaybeRef<AbortSignal | undefined>,
) => {
  return createQuery<ShoppingListResponse>({
    key: computed(() => {
      const payloadValue = unref(payload);
      const paramsValue = unref(params);
      if (!payloadValue || !paramsValue) {
        return null;
      }
      // chain_id is deliberately out of the key: it continues a search rather
      // than identifying a different one, and would break page-to-page reuse.
      return ["search", "shopping-list", payloadValue, paramsValue];
    }),
    fetcher: async () => {
      const payloadValue = unref(payload);
      const paramsValue = unref(params);
      if (!payloadValue || !paramsValue) {
        throw new Error("shopping list payload and params are required");
      }
      const response = await shoppingList(payloadValue, paramsValue, {
        chainId: unref(chainId) ?? null,
        signal: unref(signal),
      });
      return response.data;
    },
    enabled: computed(() => {
      const baseEnabled = enabled === undefined ? true : Boolean(unref(enabled));
      return (
        baseEnabled &&
        Boolean(unref(payload) && unref(params))
      );
    }),
    staleTime: 0,
    cacheTime: 0,
  });
};

